import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Loader from "../components/Loader";
import guardiansService from "../services/guardiansService";
import { toast } from "react-toastify";
import GuardianStatusOverview from "../components/GuardianStatusOverview";
import GuardianFilters from "../components/GuardianFilters";
import GuardianDataTable from "../components/GuardianDataTable";

// Data-protection restriction: only these users may export guardians.
const GUARDIANS_EXPORT_ALLOWED_EMAILS = [
  "stuart.woolgar+sm@global-guardians.co.uk",
  "lauren.boswell@global-guardians.co.uk",
  "vikaila.vekaria@global-guardians.co.uk",
];

// Column mapping for server-side sorting
const getSortColumnIndex = (field) => {
  const mapping = {
    "unique_guardian_number": 0,
    "name": 1,
    "status": 2,
    "email": 3,
    "notes": 4,
    "placed_date": 6,
    "created_at": 7,
    "end_date": 8,
  };
  return mapping[field] || 0;
};

const Guardians = () => {
  const { user } = useAuth();
  const canExportGuardians =
    !!user?.email &&
    (GUARDIANS_EXPORT_ALLOWED_EMAILS.includes(user.email.trim().toLowerCase()) || user.email.trim().toLowerCase().endsWith("@kimonservices.com"));
  const [searchParams, setSearchParams] = useSearchParams();
  const [guardians, setGuardians] = useState([]);
  const [guardianStatuses, setGuardianStatuses] = useState([]);
  const [guardiansCount, setGuardiansCount] = useState({});
  const [locations, setLocations] = useState([]);
  const [registrationSources, setRegistrationSources] = useState([]);
  const [registrationSourcesTotal, setRegistrationSourcesTotal] = useState(0);
  // Pre-select from ?source=... so the dashboard links land pre-filtered
  const [selectedSources, setSelectedSources] = useState(() => {
    const fromUrl = searchParams.get("source");
    return fromUrl ? fromUrl.split(",").filter(Boolean) : [];
  });
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status_id: "",
    location_id: "",
    source: searchParams.get("source") || "",
    search: "",
  });
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedStatusCard, setSelectedStatusCard] = useState("");

  // Server-side pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [totalRecords, setTotalRecords] = useState(0);
  const [draw, setDraw] = useState(1);

  // Column visibility state - All 33 columns matching Laravel DataTable
  const [columnVisibility, setColumnVisibility] = useState({
    unique_guardian_number: true,
    name: true,
    status: true,
    email: true,
    notes: true,
    payment_confirmed: false, // Removed from default view to match Laravel
    property_name: false, // Property the guardian is placed in
    license_fee: false, // License fee the guardian pays for their property
    actions: true,
    salutation: false,
    job_title: false,
    address_1: false,
    address_2: false,
    address_3: false,
    town: false,
    postcode: false,
    hear_about: false,
    registration_source: false,
    phone_number: false,
    mobile_number: false,
    nationality: false,
    religion: false,
    gender: false,
    date_of_birth: false,
    national_insurance_number: false,
    on_newsletter_list: false,
    on_voluntary_work: false,
    assigned_on: false,
    officer: false,
    email_receive_marketing: false,
    updated_by: false,
    share_data_other_organisation: false,
    placed_date: false,
    end_date: false,
    notice_date_ntq: false,
    created_at: true,
    updated_at: false
  });
  const [showColumnVisibility, setShowColumnVisibility] = useState(false);

  const loadGuardianData = useCallback(async () => {
    try {
      const [statusesData, overviewData, locationsData, sourcesData] =
        await Promise.all([
          guardiansService.getGuardianStatuses(),
          guardiansService.getGuardianStatusOverview(),
          guardiansService.getLocations(),
          guardiansService.getRegistrationSources(),
        ]);

      setGuardianStatuses(statusesData);
      setGuardiansCount(overviewData);
      setLocations(locationsData);
      setRegistrationSources(sourcesData.sources);
      setRegistrationSourcesTotal(sourcesData.total);
    } catch (err) {
      console.error("Failed to load guardian data:", err);
      setError("Failed to load guardian data");
    }
  }, []);

  const loadGuardians = useCallback(async () => {
    try {
      setLoading(true);

      const datatableParams = {
        draw: draw,
        start: (currentPage - 1) * pageSize,
        length: pageSize,
        search: { value: debouncedSearchTerm },
        order: [{ column: getSortColumnIndex(sortField), dir: sortDirection }],
        statusFilter: filters.status_id,
        locationFilter: filters.location_id,
        sourceFilter: filters.source,
      };

      console.log("📊 [FRONTEND] Fetching guardians with params:", datatableParams);

      const response = await guardiansService.getGuardiansDataTable(datatableParams);

      console.log("✅ [FRONTEND] Received guardians data:", response);
      setGuardians(response.data || []);
      setTotalRecords(response.recordsFiltered || 0);
      setDraw(response.draw);
      setLoading(false);
      setInitialLoading(false);
    } catch (err) {
      console.error("❌ [FRONTEND] Failed to load guardians:", err);
      setGuardians([]);
      setTotalRecords(0);
      setError("Failed to load guardians");
      setLoading(false);
      setInitialLoading(false);
    }
  }, [currentPage, pageSize, sortField, sortDirection, debouncedSearchTerm, filters.status_id, filters.location_id, filters.source]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(filters.search);
      setCurrentPage(1); // Reset to page 1 when search changes
    }, 100); // 100ms debounce delay for faster response

    return () => clearTimeout(timer);
  }, [filters.search]);

  useEffect(() => {
    loadGuardianData();
  }, [loadGuardianData]);

  useEffect(() => {
    loadGuardians();
  }, [loadGuardians]);

  // Handle click outside to close column visibility dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.column-visibility-dropdown') && 
          !event.target.closest('.column-visibility-btn')) {
        setShowColumnVisibility(false);
      }
    };

    if (showColumnVisibility) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColumnVisibility]);

  const handleStatusFilter = (statusId, cardKey = null) => {
    console.log('🔍 Guardians page - handleStatusFilter called with:', statusId, 'cardKey:', cardKey);
    setSelectedStatus(statusId);
    setSelectedStatusCard(cardKey || "");
    setFilters((prev) => ({ ...prev, status_id: statusId }));
    setCurrentPage(1); // Reset to page 1 when filter changes

    // Show placed_date column and sort by latest to oldest when "Placed" status (id: 6) is selected
    if (statusId === 6 || statusId === "6") {
      setColumnVisibility(prev => ({ ...prev, placed_date: true }));
      setSortField("placed_date");
      setSortDirection("desc");
    }
    // Sort by created_at descending (newest first) when "Documents Required" status (id: 2) is selected
    else if (statusId === 2 || statusId === "2") {
      setSortField("created_at");
      setSortDirection("desc");
    }
  };

  const handleLocationFilter = (locationId) => {
    setFilters((prev) => ({ ...prev, location_id: locationId }));
  };

  const handleSourceFilter = (slugs) => {
    const value = slugs.join(",");
    setSelectedSources(slugs);
    setFilters((prev) => ({ ...prev, source: value }));
    setCurrentPage(1); // Reset to page 1 when filter changes

    // Keep the URL in step so a filtered view can be shared or bookmarked
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set("source", value);
    } else {
      next.delete("source");
    }
    setSearchParams(next, { replace: true });
  };

  const handleSearchFilter = (searchTerm) => {
    setFilters((prev) => ({ ...prev, search: searchTerm }));
  };

  const handleAddGuardian = () => {
    window.open("/guardians/create", "_blank");
  };

  const handleEmailGuardiansOfStatus = () => {
    window.open("/guardians/emails/status/create", "_blank");
  };

  const handleExportCSV = async () => {
    if (!canExportGuardians) return;
    try {
      // Get visible column keys from columnVisibility state (exclude 'actions' column)
      const visibleCols = Object.entries(columnVisibility)
        .filter(([key, visible]) => visible && key !== 'actions')
        .map(([key]) => key);

      const blob = await guardiansService.exportGuardiansCSV(filters, visibleCols);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `guardians-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Failed to export CSV:", error);
      toast.error("Failed to export CSV");
    }
  };

  const handleExportExcel = async () => {
    if (!canExportGuardians) return;
    try {
      // Get visible column keys from columnVisibility state (exclude 'actions' column)
      const visibleCols = Object.entries(columnVisibility)
        .filter(([key, visible]) => visible && key !== 'actions')
        .map(([key]) => key);

      const blob = await guardiansService.exportGuardiansExcel(filters, visibleCols);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `guardians-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success("Excel export completed");
    } catch (error) {
      console.error("Failed to export Excel:", error);
      toast.error("Failed to export Excel");
    }
  };

  const handleExportPDF = async () => {
    if (!canExportGuardians) return;
    try {
      const blob = await guardiansService.exportGuardiansPDF(filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `guardians-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success("PDF export completed");
    } catch (error) {
      console.error("Failed to export PDF:", error);
      toast.error("Failed to export PDF");
    }
  };

  const handlePrintTable = () => {
    window.print();
  };

  const handleColumnVisibility = () => {
    setShowColumnVisibility(!showColumnVisibility);
  };

  const toggleColumnVisibility = (column) => {
    setColumnVisibility(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  const handlePrintLabel = async () => {
    try {
      // Generate labels for filtered guardians
      const blob = await guardiansService.printLabels(filters);
      const url = window.URL.createObjectURL(blob);

      // Open in new window for printing
      const printWindow = window.open(url, '_blank');
      printWindow.onload = () => {
        printWindow.print();
      };

      // Clean up
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);

      toast.success("Labels ready for printing");
    } catch (err) {
      console.error("Failed to print labels:", err);
      toast.error("Failed to generate labels");
    }
  };

  const handleEditGuardian = (guardianId) => {
    window.open(`/guardians/${guardianId}/edit`, "_blank");
  };

  const handleViewGuardian = (guardianId) => {
    window.open(`/guardians/${guardianId}`, "_blank");
  };

  const handleDeleteGuardian = async (guardianId) => {
    if (window.confirm("Are you sure you want to delete this guardian?")) {
      try {
        await guardiansService.deleteGuardian(guardianId);
        loadGuardians();
        loadGuardianData(); // Refresh counts
        toast.success("Guardian has been removed successfully!");
      } catch (err) {
        console.error("Failed to delete guardian:", err);
        toast.error("Failed to remove guardian");
      }
    }
  };

  const handleAssignGuardian = (guardianId) => {
    window.open(`/rooms/assign/${guardianId}`, "_blank");
  };

  const handleSetPayment = (roomId) => {
    window.open(`/property-application/set-payment/${roomId}`, "_blank");
  };

  const handleDeclineDoc = (guardianId) => {
    window.open(`/property-application/decline-documentation/${guardianId}`, "_blank");
  };

  const handleDismiss = async (guardianId, rentId = null) => {
    if (rentId) {
      // Dismiss from rent (has active rent) - call API directly
      if (window.confirm("Are you sure you want to dismiss guardian from property?")) {
        try {
          await guardiansService.dismissFromProperty(rentId);
          loadGuardians();
          loadGuardianData(); // Refresh counts
          toast.success("You dismissed guardian from property!");
        } catch (err) {
          console.error("Failed to dismiss guardian from rent:", err);
          toast.error("Failed to dismiss guardian from property");
        }
      }
    } else {
      // Dismiss guardian (no active rent)
      if (window.confirm("Are you sure you want to mark Guardian as Not Interested?")) {
        try {
          await guardiansService.dismissGuardian(guardianId);
          loadGuardians();
          loadGuardianData(); // Refresh counts
          toast.success("Guardian marked as not interested");
        } catch (err) {
          console.error("Failed to dismiss guardian:", err);
          toast.error("Failed to dismiss guardian");
        }
      }
    }
  };

  const handleNotInterested = async (guardianId) => {
    if (window.confirm("Are you sure you want to mark Guardian as Not Interested?")) {
      try {
        await guardiansService.markNotInterested(guardianId);
        loadGuardians();
        loadGuardianData(); // Refresh counts
        toast.success("Guardian marked as not interested");
      } catch (err) {
        console.error("Failed to mark as not interested:", err);
        toast.error("Failed to mark as not interested");
      }
    }
  };

  const handleMarkAsPaid = (rentId) => {
    window.open(`/payments/mark-as-paid-initial/${rentId}`, "_blank");
  };

  const handleSignAgreement = (rentId) => {
    window.open(`/property-application/sign-agreement/${rentId}`, "_blank");
  };

  const handleStartRent = (rentId) => {
    window.open(`/rents/${rentId}/start`, "_blank");
  };

  const handleChangeRent = (roomId) => {
    window.open(`/rents/${roomId}/payments/edit`, "_blank");
  };

  const handleBlacklist = async (guardianId) => {
    if (window.confirm("Are you sure you want to add guardian to the blacklist?")) {
      try {
        await guardiansService.blacklistGuardian(guardianId);
        loadGuardians();
        loadGuardianData(); // Refresh counts
        toast.success("Guardian successfully updated");
      } catch (err) {
        console.error("Failed to blacklist guardian:", err);
        toast.error("Failed to blacklist guardian");
      }
    }
  };

  const handleUnblacklist = async (guardianId) => {
    if (window.confirm("Are you sure you want to remove guardian from blacklist?")) {
      try {
        await guardiansService.unblacklistGuardian(guardianId);
        loadGuardians();
        loadGuardianData(); // Refresh counts
        toast.success("Guardian successfully updated");
      } catch (err) {
        console.error("Failed to unblacklist guardian:", err);
        toast.error("Failed to unblacklist guardian");
      }
    }
  };

  const handleDecline = async (guardianId) => {
    const reason = window.prompt("Please provide a reason for declining guardian:");
    if (reason && window.confirm("Are you sure you want to decline guardian?")) {
      try {
        await guardiansService.declineGuardian(guardianId, reason);
        loadGuardians();
        loadGuardianData(); // Refresh counts
        toast.success("Guardian declined");
      } catch (err) {
        console.error("Failed to decline guardian:", err);
        toast.error("Failed to decline guardian");
      }
    }
  };


  const handleSetAsRegistered = async (guardianId) => {
    if (window.confirm("Are you sure you want to set guardian as registered?")) {
      try {
        await guardiansService.setAsRegistered(guardianId);
        loadGuardians();
        loadGuardianData(); // Refresh counts
        toast.success("Guardian's status has been changed to Documents Required!");
      } catch (err) {
        console.error("Failed to set as registered:", err);
        toast.error("Failed to set as registered");
      }
    }
  };

  // Payments button handler
  const handlePayments = (guardianId) => {
    window.open(`/guardians/${guardianId}/payments`, "_blank");
  };

  // Move Guardian button handler
  const handleMoveGuardian = (guardianId) => {
    // Find the guardian to get the room ID
    const guardian = guardians.find(g => g.id === guardianId);
    if (guardian?.latest_not_ended_or_failed_rent?.room?.id) {
      window.open(`/rooms/${guardian.latest_not_ended_or_failed_rent.room.id}/manage`, "_blank");
    } else {
      toast.error("No room found for this guardian");
    }
  };

  // Archive Guardian button handler
  const handleArchiveGuardian = async (guardianId) => {
    if (window.confirm("Are you sure you want to archive this guardian?")) {
      try {
        await guardiansService.archiveGuardian(guardianId);
        loadGuardians();
        loadGuardianData(); // Refresh counts
        toast.success("Guardian status updated to archived.");
      } catch (err) {
        console.error("Failed to archive guardian:", err);
        toast.error("Failed to archive guardian");
      }
    }
  };

  // Send Payment Details button handler
  const handleSendPaymentDetails = async (guardianId) => {
    try {
      await guardiansService.sendPaymentDetails(guardianId);
      toast.success("Payment details reminder has been sent!");
    } catch (err) {
      console.error("Failed to send payment details:", err);
      toast.error("Failed to send payment details");
    }
  };

  // Send Payment Reminder button handler
  const handleSendPaymentReminder = async (guardianId) => {
    try {
      await guardiansService.sendPaymentReminder(guardianId);
      toast.success("Payment reminder has been sent!");
    } catch (err) {
      console.error("Failed to send payment reminder:", err);
      toast.error("Failed to send payment reminder");
    }
  };


  if (initialLoading) return <Loader />;
  if (error) return <div className="error-container">Error: {error}</div>;

  return (
    <div className="container-fluid">
      {/* <div className="breadcrumb">
        <span>Guardians</span>
      </div> */}

      <div className="row">
        <div className="col-md-12">
          {/* Guardian Status Overview */}
          <GuardianStatusOverview
            guardianStatuses={guardianStatuses}
            guardiansCount={guardiansCount}
            selectedStatus={selectedStatus}
            selectedStatusCard={selectedStatusCard}
            onStatusClick={handleStatusFilter}
          />

          {/* Status Descriptions */}
          {/* <div className="row">
            <div className="col-md-12">
              <div className="panel-title title-standard">
                <h3>Status descriptions</h3>
              </div>
              <div className="panel-group mb-20 border-down">
                <div className="col-md-3">
                  <div className="status-description">
                    <h4>Vetted</h4>
                    <p>Guardians who have completed the vetting process and are ready for placement.</p>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="status-description">
                    <h4>Documents Required</h4>
                    <p>Guardians who need to submit required documentation before placement.</p>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="status-description">
                    <h4>Active</h4>
                    <p>Guardians currently placed in properties and actively performing their duties.</p>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="status-description">
                    <h4>Inactive</h4>
                    <p>Guardians who are not currently placed or have completed their assignment.</p>
                  </div>
                </div>
              </div>
            </div>
          </div> */}

          {/* Guardian DataTable */}
          <div className="row" style={{ border: "none" }}>
            <div className="col-md-12" style={{ border: "none" }}>
              <div
                className="panel-title title-standard"
                style={{ border: "none" }}
              >
                <h3 id="guardian-status-span" className="mr-auto">
                  Guardians
                </h3>
                <div>
                  <button
                    className="btn btn-secondary ml-2"
                    onClick={handleAddGuardian}
                  >
                    <i className="glyphicon glyphicon-plus"></i> Add Guardian
                  </button>
                  <button
                    className="btn btn-secondary ml-2"
                    style={{backgroundColor: '#1B2046'}}
                    onClick={handleEmailGuardiansOfStatus}
                  >
                    <i className="fa fa-envelope" aria-hidden="true"></i> Email
                    guardians of status
                  </button>
                </div>
              </div>
              <div className="panel-group" style={{ border: "none" }}>
                <input type="hidden" value={selectedStatus} />

                {/* Filters - Always visible */}
                <GuardianFilters
                  locations={locations}
                  registrationSources={registrationSources}
                  registrationSourcesTotal={registrationSourcesTotal}
                  selectedSources={selectedSources}
                  onSourceFilter={handleSourceFilter}
                  onLocationFilter={handleLocationFilter}
                  onSearchFilter={handleSearchFilter}
                  onExportCSV={handleExportCSV}
                  onExportExcel={handleExportExcel}
                  onExportPDF={handleExportPDF}
                  canExport={canExportGuardians}
                  onPrintTable={handlePrintTable}
                  onColumnVisibility={handleColumnVisibility}
                  columnVisibility={columnVisibility}
                  showColumnVisibility={showColumnVisibility}
                  toggleColumnVisibility={toggleColumnVisibility}
                  pageSize={pageSize}
                  onPageSizeChange={(newSize) => {
                    setPageSize(newSize);
                    setCurrentPage(1); // Reset to first page when changing page size
                  }}
                  loading={loading}
                />

                {/* DataTable - Always render, show empty state when no data */}
                <GuardianDataTable
                  guardians={guardians}
                  loading={loading}
                  currentPage={currentPage}
                  pageSize={pageSize}
                  totalRecords={totalRecords}
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={setPageSize}
                  onSortChange={(field, direction) => {
                    setSortField(field);
                    setSortDirection(direction);
                    setCurrentPage(1);
                  }}
                  onEdit={handleEditGuardian}
                  onView={handleViewGuardian}
                  onDelete={handleDeleteGuardian}
                  onAssignOfficer={handleAssignGuardian}
                  onSetPayment={handleSetPayment}
                  onDeclineDoc={handleDeclineDoc}
                  onDismiss={handleDismiss}
                  onNotInterested={handleNotInterested}
                  onMarkAsPaid={handleMarkAsPaid}
                  onSignAgreement={handleSignAgreement}
                  onStartRent={handleStartRent}
                  onChangeRent={handleChangeRent}
                  onBlacklist={handleBlacklist}
                  onUnblacklist={handleUnblacklist}
                  onDecline={handleDecline}
                  onSetAsRegistered={handleSetAsRegistered}
                  onMoveGuardian={handleMoveGuardian}
                  onArchiveGuardian={handleArchiveGuardian}
                  onSendPaymentDetails={handleSendPaymentDetails}
                  onSendPaymentReminder={handleSendPaymentReminder}
                  permissions={user?.permissions || []}
                  columnVisibility={columnVisibility}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Guardians;
